export const BILLING_DIRECTORY_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "created", label: "Entreprises créées" },
  { value: "needs_setup", label: "Clients à rattacher" },
];
export const BILLING_DIRECTORY_TYPES = [
  { value: "all", label: "Tous" },
  { value: "coops", label: "Entreprises facturées" },
];

const text = (value) => String(value ?? "").trim();
const searchable = (value) => text(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export const billingDirectoryClientName = (client) => text(client.client_name || client.correspondence_name || client.client_code) || "Client sans nom";
export const billingDirectoryOwner = (client) => client.billing_account_id ?? client.billing_account?.id ?? null;
const compare = (a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base", numeric: true }) || a.key.localeCompare(b.key);

// IDs and saved ownership are authoritative. Equal names never imply an association.
export function buildBillingDirectory({ accounts = [], sourceClients = [] } = {}) {
  const byAccount = new Map(accounts.filter((account) => account.id && account.is_active !== false).map((account) => [account.id, {
    key: `account:${account.id}`, kind: "account", account, clients: [],
    name: text(account.name || account.legal_name), address: text(account.billing_address), email: text(account.billing_email),
  }]));
  const pending = [];
  for (const client of new Map(sourceClients.filter((row) => row.id).map((row) => [row.id, row])).values()) {
    const owner = byAccount.get(billingDirectoryOwner(client));
    if (owner) owner.clients.push(client);
    // The Coop pays: only Coop records wait for a company.
    else if (client.is_coop === true) pending.push({ key: `client:${client.id}`, kind: "client", client, clients: [client],
      name: billingDirectoryClientName(client), address: text(client.address), email: "", status: "needs_setup" });
  }
  const rows = [...byAccount.values()].map((row) => {
    row.clients.sort((a, b) => billingDirectoryClientName(a).localeCompare(billingDirectoryClientName(b), "fr"));
    return { ...row, status: "created" };
  });
  return [...rows, ...pending].sort(compare);
}

export function filterBillingDirectory(rows, { search = "", status = "all", type = "all" } = {}) {
  const needle = searchable(search);
  const matching = rows.filter((row) => !needle || searchable([
    row.name, row.address, row.email,
    ...row.clients.flatMap((client) => [client.client_name, client.correspondence_name, client.client_code, client.address, client.email, client.billing_email]),
  ].join(" ")).includes(needle));
  const matchesType = (row, value) => value === "all" || row.clients.some((client) => client.is_coop === true);
  const matchesStatus = (row, value) => value === "all" || row.status === value;
  const counts = Object.fromEntries(BILLING_DIRECTORY_FILTERS.map(({ value }) =>
    [value, matching.filter(row => matchesType(row, type) && matchesStatus(row, value)).length]));
  const typeCounts = Object.fromEntries(BILLING_DIRECTORY_TYPES.map(({ value }) =>
    [value, matching.filter(row => matchesStatus(row, status) && matchesType(row, value)).length]));
  return { rows: matching.filter(row => matchesType(row, type) && matchesStatus(row, status)), counts, typeCounts };
}

export function billingDirectoryAccountDraft(client) {
  return { name: billingDirectoryClientName(client), billing_address: text(client.address), client_ids: [client.id] };
}

export function billingDirectoryAssociationPayload(client, account) {
  // This action only links an unassigned client. Transfers use the associations editor.
  if (billingDirectoryOwner(client) || !account?.id || account.is_active === false) {
    throw new Error("L’association a changé. Rechargez le répertoire avant de réessayer.");
  }
  return {
    accounts: [{ account_id: account.id, expected_version: account.version }],
    clients: [{ client_id: client.id, expected_billing_account_id: null, billing_account_id: account.id }],
  };
}
